import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { MID_MATCH_DEFAULT_VALIDITY, PAGE_KEYS, PREFERENCE_KEYS, PRICE_COLUMN_ORDERS } from 'lib/constants';
import { createUserPreference, getUserPreference } from 'services/user-preference.service';

const parseBooleanValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (value == null) return false;
    return String(value).toLowerCase() === 'true';
};

const findPreference = (entities, pageKey, preferenceKey) => {
    if (!Array.isArray(entities)) return null;
    return entities.find(
        (entry) => entry?.page_key === pageKey && entry?.preference_key === preferenceKey
    ) ?? null;
};

const DEFAULT_MATCHING_LHS_CURRENCY = { active: 'AUD', recency: [] };

const parseMatchingLhsCurrency = (raw) => {
    if (!raw) return { ...DEFAULT_MATCHING_LHS_CURRENCY };
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const active = typeof parsed?.active === 'string' && parsed.active
            ? parsed.active
            : DEFAULT_MATCHING_LHS_CURRENCY.active;
        const recency = Array.isArray(parsed?.recency)
            ? parsed.recency.filter((c) => typeof c === 'string' && c)
            : [];
        return { active, recency };
    } catch (_) {
        return { ...DEFAULT_MATCHING_LHS_CURRENCY };
    }
};

const nextRecency = (currency, prevRecency) => {
    const filtered = (prevRecency ?? []).filter((c) => c !== currency);
    return [currency, ...filtered];
};

const DEFAULT_VALIDITY = {};

const parseDefaultValidity = (raw) => {
    if (raw == null) return { ...DEFAULT_VALIDITY };
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_VALIDITY };
        return Object.fromEntries(
            Object.entries(parsed)
                .filter(([, v]) => v != null && v !== '' && Number.isFinite(Number(v)))
                .map(([k, v]) => [k, Number(v)])
        );
    } catch (_) {
        return { ...DEFAULT_VALIDITY };
    }
};

// null = default-all-ON (preference absent). An empty array is a valid saved state (all curves off).
const parseSelectedCurves = (raw) => {
    if (raw == null) return null;
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return Array.isArray(parsed) ? parsed.filter((c) => typeof c === 'string') : null;
    } catch (_) {
        return null;
    }
};

// Anything unrecognised falls back to the default rather than leaving the CLOB
// with a column order it cannot render.
const parsePriceColumnOrder = (raw) => (raw === PRICE_COLUMN_ORDERS.OFFER_BID
    ? PRICE_COLUMN_ORDERS.OFFER_BID
    : PRICE_COLUMN_ORDERS.BID_OFFER);

// Group all Curves and the price column order are saved per CLOB market (AGY / IDB).
const CLOB_MARKETS = ['AGENCY', 'IDB'];
const marketPreferenceKey = (key, brokerType) => `${key}-${brokerType.toLowerCase()}`;
const byMarket = (read) => Object.fromEntries(CLOB_MARKETS.map((market) => [market, read(market)]));

const useUserPreferenceStore = create(immer((set, get) => ({
    domesticConvention: false,
    matchingLhsCurrency: { ...DEFAULT_MATCHING_LHS_CURRENCY },
    dashboardSelectedCurves: null,
    selectedFxVolPairs: null,
    selectedFxFwdPairs: null,
    defaultValidity: { ...DEFAULT_VALIDITY },
    midMatchDefaultValidity: { ...MID_MATCH_DEFAULT_VALIDITY },
    // Last interest validity the user picked from a chip row, reused on the next
    // submit. null = never picked, so Mid Match falls back to its class default.
    lastInterestValidity: null,
    groupAllCurves: byMarket(() => false),
    // Latest-activity panel open/closed on the CLOB dashboard. Persisted per user.
    activityPanelOpen: false,
    // The saved price-column-order preference per market, or null when the user has
    // none. Null lets the CLOB pick a persona default (IDB brokers start on offer
    // first); a saved 'bidOffer'/'offerBid' always wins. Persisted per user.
    priceColumnOrder: byMarket(() => null),
    // The CLOB active trader — the trader a broker is currently pricing for.
    // Only meaningful for broker users (broker view). Persisted per user.
    activeTrader: null,
    hydrated: false,
    hydrating: false,

    hydrate: async () => {
        if (get().hydrated || get().hydrating) return;
        set((state) => { state.hydrating = true; });
        const response = await getUserPreference();
        const entities = response?.data?.entity ?? response?.data?.data ?? [];
        const domesticPref = findPreference(
            entities,
            PAGE_KEYS.GLOBAL,
            PREFERENCE_KEYS.DOMESTIC_CONVENTION
        );
        const matchingCurrencyPref = findPreference(
            entities,
            PAGE_KEYS.MATCHING,
            PREFERENCE_KEYS.MATCHING_LHS_CURRENCY
        );
        const dashboardCurvesPref = findPreference(
            entities,
            PAGE_KEYS.DASHBOARD_WATCHLIST,
            PREFERENCE_KEYS.SELECTED_CURVES
        );
        const fxVolPairsPref = findPreference(
            entities,
            PAGE_KEYS.DASHBOARD_WATCHLIST,
            PREFERENCE_KEYS.SELECTED_FX_VOL_PAIRS
        );
        const fxFwdPairsPref = findPreference(
            entities,
            PAGE_KEYS.DASHBOARD_WATCHLIST,
            PREFERENCE_KEYS.SELECTED_FXFWD_PAIRS
        );
        const defaultValidityPref = findPreference(
            entities,
            PAGE_KEYS.CLOB,
            PREFERENCE_KEYS.DEFAULT_VALIDITY
        );
        const midMatchValidityPref = findPreference(
            entities,
            PAGE_KEYS.CLOB,
            PREFERENCE_KEYS.MID_MATCH
        );
        const clobMarketPref = (key, market) => findPreference(
            entities,
            PAGE_KEYS.CLOB,
            marketPreferenceKey(key, market)
        );
        const activeTraderPref = findPreference(
            entities,
            PAGE_KEYS.CLOB,
            PREFERENCE_KEYS.CLOB_ACTIVE_CHIP
        );
        const lastValidityPref = findPreference(
            entities,
            PAGE_KEYS.CLOB,
            PREFERENCE_KEYS.LAST_INTEREST_VALIDITY
        );
        const activityPanelPref = findPreference(
            entities,
            PAGE_KEYS.CLOB,
            PREFERENCE_KEYS.ACTIVITY_PANEL_OPEN
        );
        set((state) => {
            state.domesticConvention = parseBooleanValue(domesticPref?.preference_value);
            state.matchingLhsCurrency = parseMatchingLhsCurrency(matchingCurrencyPref?.preference_value);
            state.dashboardSelectedCurves = parseSelectedCurves(dashboardCurvesPref?.preference_value);
            state.selectedFxVolPairs = parseSelectedCurves(fxVolPairsPref?.preference_value);
            state.selectedFxFwdPairs = parseSelectedCurves(fxFwdPairsPref?.preference_value);
            state.defaultValidity = parseDefaultValidity(defaultValidityPref?.preference_value);
            state.midMatchDefaultValidity = {
                ...MID_MATCH_DEFAULT_VALIDITY,
                ...parseDefaultValidity(midMatchValidityPref?.preference_value)
            };
            state.groupAllCurves = byMarket((market) => parseBooleanValue(
                clobMarketPref(PREFERENCE_KEYS.GROUP_ALL_CURVES, market)?.preference_value
            ));
            state.activityPanelOpen = parseBooleanValue(activityPanelPref?.preference_value);
            // No saved row → stay null so the CLOB applies its persona default.
            state.priceColumnOrder = byMarket((market) => {
                const pref = clobMarketPref(PREFERENCE_KEYS.PRICE_COLUMN_ORDER, market);
                return pref ? parsePriceColumnOrder(pref.preference_value) : null;
            });
            state.activeTrader = activeTraderPref?.preference_value ?? null;
            const parsedLast = Number(lastValidityPref?.preference_value);
            state.lastInterestValidity = lastValidityPref && Number.isFinite(parsedLast) ? parsedLast : null;
            state.hydrated = true;
            state.hydrating = false;
        });
    },

    setLastInterestValidity: async (nextValue) => {
        const value = Number(nextValue);
        if (!Number.isFinite(value)) return { status: false };
        const previous = get().lastInterestValidity;
        if (previous === value) return { status: true };
        set((state) => { state.lastInterestValidity = value; });
        const response = await createUserPreference({
            page_key: PAGE_KEYS.CLOB,
            preference_key: PREFERENCE_KEYS.LAST_INTEREST_VALIDITY,
            preference_value: String(value)
        });
        if (!response?.status) {
            set((state) => { state.lastInterestValidity = previous; });
        }
        return response;
    },

    setDomesticConvention: async (nextValue) => {
        const value = Boolean(nextValue);
        const previous = get().domesticConvention;
        if (previous === value) return { status: true };

        set((state) => { state.domesticConvention = value; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.GLOBAL,
            preference_key: PREFERENCE_KEYS.DOMESTIC_CONVENTION,
            preference_value: String(value)
        });

        if (!response?.status) {
            set((state) => { state.domesticConvention = previous; });
        }

        return response;
    },

    setMatchingLhsCurrency: async (nextActive) => {
        const currency = typeof nextActive === 'string' ? nextActive : null;
        if (!currency) return { status: false };

        const previous = get().matchingLhsCurrency;
        if (previous.active === currency && previous.recency?.[0] === currency) {
            return { status: true };
        }

        const optimistic = {
            active: currency,
            recency: nextRecency(currency, previous.recency)
        };

        set((state) => { state.matchingLhsCurrency = optimistic; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.MATCHING,
            preference_key: PREFERENCE_KEYS.MATCHING_LHS_CURRENCY,
            preference_value: JSON.stringify(optimistic)
        });

        if (!response?.status) {
            set((state) => { state.matchingLhsCurrency = previous; });
        }

        return response;
    },

    setDashboardSelectedCurves: async (nextCurves) => {
        const curves = Array.isArray(nextCurves)
            ? nextCurves.filter((c) => typeof c === 'string')
            : null;
        const previous = get().dashboardSelectedCurves;

        set((state) => { state.dashboardSelectedCurves = curves; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.DASHBOARD_WATCHLIST,
            preference_key: PREFERENCE_KEYS.SELECTED_CURVES,
            preference_value: JSON.stringify(curves ?? [])
        });

        if (!response?.status) {
            set((state) => { state.dashboardSelectedCurves = previous; });
        }
        return response;
    },

    setSelectedFxVolPairs: async (nextPairs) => {
        const pairs = Array.isArray(nextPairs)
            ? nextPairs.filter((c) => typeof c === 'string')
            : null;
        const previous = get().selectedFxVolPairs;

        set((state) => { state.selectedFxVolPairs = pairs; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.DASHBOARD_WATCHLIST,
            preference_key: PREFERENCE_KEYS.SELECTED_FX_VOL_PAIRS,
            preference_value: JSON.stringify(pairs ?? [])
        });

        if (!response?.status) {
            set((state) => { state.selectedFxVolPairs = previous; });
        }
        return response;
    },
    setDefaultValidity: async (nextValues) => {
        if (!nextValues || typeof nextValues !== 'object') return { status: false };
        const previous = get().defaultValidity;
        const next = parseDefaultValidity(nextValues);

        set((state) => { state.defaultValidity = next; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.CLOB,
            preference_key: PREFERENCE_KEYS.DEFAULT_VALIDITY,
            preference_value: JSON.stringify(next)
        });

        if (!response?.status) {
            set((state) => { state.defaultValidity = previous; });
        }
        return response;
    },

    setMidMatchDefaultValidity: async (nextValues) => {
        if (!nextValues || typeof nextValues !== 'object') return { status: false };
        const previous = get().midMatchDefaultValidity;
        const next = { ...MID_MATCH_DEFAULT_VALIDITY, ...parseDefaultValidity(nextValues) };

        set((state) => { state.midMatchDefaultValidity = next; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.CLOB,
            preference_key: PREFERENCE_KEYS.MID_MATCH,
            preference_value: JSON.stringify(next)
        });

        if (!response?.status) {
            set((state) => { state.midMatchDefaultValidity = previous; });
        }
        return response;
    },

    setSelectedFxFwdPairs: async (nextPairs) => {
        const pairs = Array.isArray(nextPairs)
            ? nextPairs.filter((c) => typeof c === 'string')
            : null;
        const previous = get().selectedFxFwdPairs;

        set((state) => { state.selectedFxFwdPairs = pairs; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.DASHBOARD_WATCHLIST,
            preference_key: PREFERENCE_KEYS.SELECTED_FXFWD_PAIRS,
            preference_value: JSON.stringify(pairs ?? [])
        });

        if (!response?.status) {
            set((state) => { state.selectedFxFwdPairs = previous; });
        }
        return response;
    },
    setActiveTrader: async (nextCode) => {
        const code = typeof nextCode === 'string' && nextCode ? nextCode : null;
        if (!code) return { status: true };
        const previous = get().activeTrader;
        if (previous === code) return { status: true };

        set((state) => { state.activeTrader = code; });

        return createUserPreference({
            page_key: PAGE_KEYS.CLOB,
            preference_key: PREFERENCE_KEYS.CLOB_ACTIVE_CHIP,
            preference_value: code
        });
    },

    setGroupAllCurves: async (brokerType, nextValue) => {
        const value = Boolean(nextValue);
        const previous = get().groupAllCurves[brokerType];
        if (previous === value) return { status: true };

        set((state) => { state.groupAllCurves[brokerType] = value; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.CLOB,
            preference_key: marketPreferenceKey(PREFERENCE_KEYS.GROUP_ALL_CURVES, brokerType),
            preference_value: String(value)
        });

        if (!response?.status) {
            set((state) => { state.groupAllCurves[brokerType] = previous; });
        }
        return response;
    },

    setActivityPanelOpen: async (nextValue) => {
        const value = Boolean(nextValue);
        const previous = get().activityPanelOpen;
        if (previous === value) return { status: true };

        set((state) => { state.activityPanelOpen = value; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.CLOB,
            preference_key: PREFERENCE_KEYS.ACTIVITY_PANEL_OPEN,
            preference_value: String(value)
        });

        if (!response?.status) {
            set((state) => { state.activityPanelOpen = previous; });
        }
        return response;
    },
    setPriceColumnOrder: async (brokerType, nextValue) => {
        const value = parsePriceColumnOrder(nextValue);
        const previous = get().priceColumnOrder[brokerType];
        if (previous === value) return { status: true };

        set((state) => { state.priceColumnOrder[brokerType] = value; });

        const response = await createUserPreference({
            page_key: PAGE_KEYS.CLOB,
            preference_key: marketPreferenceKey(PREFERENCE_KEYS.PRICE_COLUMN_ORDER, brokerType),
            preference_value: value
        });

        if (!response?.status) {
            set((state) => { state.priceColumnOrder[brokerType] = previous; });
        }
        return response;
    },

    resetUserPreference: () => set((state) => {
        state.domesticConvention = false;
        state.matchingLhsCurrency = { ...DEFAULT_MATCHING_LHS_CURRENCY };
        state.dashboardSelectedCurves = null;
        state.selectedFxVolPairs = null;
        state.selectedFxFwdPairs = null;
        state.defaultValidity = { ...DEFAULT_VALIDITY };
        state.midMatchDefaultValidity = { ...MID_MATCH_DEFAULT_VALIDITY };
        state.lastInterestValidity = null;
        state.groupAllCurves = byMarket(() => false);
        state.activityPanelOpen = false;
        state.priceColumnOrder = byMarket(() => null);
        state.activeTrader = null;
        state.hydrated = false;
        state.hydrating = false;
    })
})));

export default useUserPreferenceStore;
export { useUserPreferenceStore };
